/**
 * usePullToRefresh — Lightweight, dependency-free pull-to-refresh for mobile.
 * Activates only when the page is at scrollY=0 and on touch devices.
 */
import { useEffect, useRef, useState } from 'react';

interface Options {
  onRefresh: () => Promise<unknown> | void;
  /** Distance in px the user must pull before release triggers refresh */
  threshold?: number;
  /** Max visual pull distance (rubber-band cap) */
  maxPull?: number;
  /** Disable on desktop / when false */
  enabled?: boolean;
}

export function usePullToRefresh({ onRefresh, threshold = 70, maxPull = 120, enabled = true }: Options) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const active = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    const onTouchStart = (e: TouchEvent) => {
      if (window.scrollY > 0) return;
      startY.current = e.touches[0].clientY;
      active.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!active.current || startY.current == null || refreshing) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta <= 0) {
        setPull(0);
        return;
      }
      // Rubber-band: dampen pull
      const damped = Math.min(maxPull, delta * 0.5);
      setPull(damped);
    };

    const onTouchEnd = async () => {
      if (!active.current) return;
      active.current = false;
      startY.current = null;
      if (pull >= threshold && !refreshing) {
        setRefreshing(true);
        setPull(threshold);
        try {
          await onRefresh();
        } finally {
          setRefreshing(false);
          setPull(0);
        }
      } else {
        setPull(0);
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enabled, pull, threshold, maxPull, refreshing, onRefresh]);

  const progress = Math.min(1, pull / threshold);
  return { pull, progress, refreshing };
}
